import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appointmentService, userService, workScheduleService } from '../services/api';
import { ChevronLeft, ChevronRight, Clock, User, Phone, MapPin, Calendar, CheckCircle } from 'lucide-react';

// Função auxiliar para formatar data local YYYY-MM-DD sem distorção de fuso horário
const formatLocalDate = (date) => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AppointmentCalendar = ({ selectedUserId, onDateClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  // Calcular início e fim do mês atual
  const monthStart = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return new Date(year, month, 1);
  }, [currentDate]);

  const monthEnd = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return new Date(year, month + 1, 0, 23, 59, 59);
  }, [currentDate]);

  // Buscar agendamentos do mês
  const { data: appointmentsData, isLoading } = useQuery({
    queryKey: ['appointments-calendar', selectedUserId, monthStart.toISOString(), monthEnd.toISOString()],
    queryFn: () => appointmentService.getAppointments({
      responsible_user_id: selectedUserId || '',
      date_from: monthStart.toISOString(),
      date_to: monthEnd.toISOString(),
      limit: 1000 // Buscar todos do mês
    }),
    enabled: !!monthStart && !!monthEnd,
    staleTime: 30000
  });

  // Buscar usuários para o seletor
  const { data: usersData } = useQuery({
    queryKey: ['users-calendar'],
    queryFn: () => userService.getUsers({ limit: 100 }),
    staleTime: 5 * 60 * 1000
  });

  // Buscar jornadas de trabalho do usuário selecionado
  const { data: schedulesData } = useQuery({
    queryKey: ['work-schedules-calendar', selectedUserId],
    queryFn: () => workScheduleService.getWorkSchedules({
      user_id: selectedUserId || undefined,
      is_active: true
    }),
    enabled: !!selectedUserId,
    staleTime: 5 * 60 * 1000,
  });

  // Garantir que appointments seja sempre um array
  // A API retorna { data: [...], pagination: {...} }
  const appointments = useMemo(() => {
    if (!appointmentsData) return [];
    const data = appointmentsData.data;
    if (Array.isArray(data)) {
      return data;
    }
    // Se data for um objeto com propriedade data (estrutura aninhada)
    if (data && typeof data === 'object' && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  }, [appointmentsData]);
  
  const users = Array.isArray(usersData?.data) ? usersData.data : [];

  // Extrair horário do appointment de forma consistente (sem conversão de timezone)
  const getAppointmentTimeKey = (apt) => {
    // Se a string contém offset (-03:00), extrair direto
    // Formato esperado: "2026-08-14T15:00:00-03:00" ou "2026-08-14T15:00:00.000Z"
    const str = apt.appointment_date;
    const match = str.match(/T(\d{2}):(\d{2})/);
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
    // Fallback: se não conseguir extrair, usar Date
    const date = new Date(str);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // Converter appointment_date para Date local corretamente
  const parseAppointmentToLocalDate = (apt) => {
    const str = apt.appointment_date;
    const dateMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (dateMatch) {
      const [, year, month, day, hour, min] = dateMatch.map(Number);
      return new Date(year, month - 1, day, hour, min, 0, 0);
    }
    return new Date(str);
  };

  // Organizar agendamentos por data e hora
  const appointmentsByDate = useMemo(() => {
    const grouped = {};
    if (!Array.isArray(appointments)) {
      return grouped;
    }
    appointments.forEach(apt => {
      const dateKey = apt.appointment_date.split('T')[0]; // YYYY-MM-DD direto da string
      const timeKey = getAppointmentTimeKey(apt);

      if (!grouped[dateKey]) {
        grouped[dateKey] = {};
      }

      if (!grouped[dateKey][timeKey]) {
        grouped[dateKey][timeKey] = [];
      }

      grouped[dateKey][timeKey].push(apt);
    });
    return grouped;
  }, [appointments]);

  // Gerar dias do mês
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Domingo, 6 = Sábado

    const days = [];

    // Dias do mês anterior (para preencher a primeira semana)
    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
        isToday: false
      });
    }

    // Dias do mês atual
    const today = new Date();
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.toDateString() === today.toDateString()
      });
    }

    // Dias do próximo mês (para completar a última semana)
    const remainingDays = 42 - days.length; // 6 semanas * 7 dias
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
        isToday: false
      });
    }

    return days;
  }, [currentDate]);

  // Calcular horários disponíveis para cada dia do mês
  const getAvailableSlotsForDay = useMemo(() => {
    if (!selectedUserId || !schedulesData?.data) return {};
    
    const schedules = Array.isArray(schedulesData.data) 
      ? schedulesData.data 
      : Array.isArray(schedulesData.data?.data)
      ? schedulesData.data.data
      : [];

    const slotsByDate = {};
    
    // Para cada dia do mês, calcular slots disponíveis
    calendarDays.forEach(day => {
      if (!day.isCurrentMonth) return;
      
      const dayOfWeek = day.date.getDay();
      const daySchedule = schedules.find(s => s.day_of_week === dayOfWeek && s.is_active);
      
      if (!daySchedule) {
        slotsByDate[formatLocalDate(day.date)] = [];
        return;
      }

      // Obter agendamentos do dia
      const dateKey = formatLocalDate(day.date);
      const dayAppointments = appointmentsByDate[dateKey] || {};
      const appointmentList = Object.values(dayAppointments).flat();
      
      // Converter horários de trabalho
      const [startHour, startMin] = daySchedule.start_time.split(':').map(Number);
      const [endHour, endMin] = daySchedule.end_time.split(':').map(Number);
      
      const workStart = new Date(day.date);
      workStart.setHours(startHour, startMin, 0, 0);
      
      const workEnd = new Date(day.date);
      workEnd.setHours(endHour, endMin, 0, 0);

      // Considerar intervalo de almoço
      let breakStart = null;
      let breakEnd = null;
      if (daySchedule.break_start_time && daySchedule.break_end_time) {
        const [breakStartHour, breakStartMin] = daySchedule.break_start_time.split(':').map(Number);
        const [breakEndHour, breakEndMin] = daySchedule.break_end_time.split(':').map(Number);
        
        breakStart = new Date(day.date);
        breakStart.setHours(breakStartHour, breakStartMin, 0, 0);
        
        breakEnd = new Date(day.date);
        breakEnd.setHours(breakEndHour, breakEndMin, 0, 0);
      }

      // Gerar slots de 15 em 15 minutos
      const slots = [];
      const slotDuration = 15;
      let currentTime = new Date(workStart);

      while (currentTime < workEnd) {
        const slotEnd = new Date(currentTime.getTime() + slotDuration * 60000);
        
        const isInBreak = breakStart && breakEnd && 
                         currentTime < breakEnd && 
                         slotEnd > breakStart;
        
        const hasConflict = appointmentList.some(apt => {
          const aptStart = parseAppointmentToLocalDate(apt);
          const aptEnd = new Date(aptStart.getTime() + apt.duration_minutes * 60000);
          return (currentTime >= aptStart && currentTime < aptEnd) ||
                 (slotEnd > aptStart && slotEnd <= aptEnd) ||
                 (currentTime <= aptStart && slotEnd >= aptEnd);
        });

        if (!isInBreak && !hasConflict && slotEnd <= workEnd) {
          slots.push({
            time: new Date(currentTime),
            formatted: currentTime.toLocaleTimeString('pt-BR', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })
          });
        }

        currentTime = new Date(currentTime.getTime() + slotDuration * 60000);
      }

      slotsByDate[dateKey] = slots;
    });

    return slotsByDate;
  }, [selectedUserId, schedulesData, appointmentsByDate, calendarDays]);

  // Navegar para mês anterior
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  // Navegar para próximo mês
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Ir para mês atual
  const goToCurrentMonth = () => {
    setCurrentDate(new Date());
  };

  // Obter agendamentos de um dia específico
  const getDayAppointments = (date) => {
    const dateKey = formatLocalDate(date);
    return appointmentsByDate[dateKey] || {};
  };

  // Contar agendamentos de um dia
  const getDayAppointmentCount = (date) => {
    const dateKey = formatLocalDate(date);
    const dayApps = appointmentsByDate[dateKey] || {};
    return Object.values(dayApps).reduce((total, timeSlots) => total + timeSlots.length, 0);
  };

  // Obter horários ocupados de um dia (para exibir em tooltip ou detalhes)
  const getDayTimeSlots = (date) => {
    const dayApps = getDayAppointments(date);
    const slots = [];

    Object.keys(dayApps).sort().forEach(time => {
      dayApps[time].forEach(apt => {
        slots.push({
          time,
          appointment: apt
        });
      });
    });

    return slots;
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const handleDateClick = (date) => {
    setSelectedDate(date);
    if (onDateClick) {
      onDateClick(date, getDayTimeSlots(date));
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 sm:p-6">
      {/* Header do Calendário */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          {selectedUserId && (
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {users.find(u => u.id === parseInt(selectedUserId))?.full_name || 'Usuário'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={goToPreviousMonth}
            className="p-2 hover:bg-white rounded-md transition-all shadow-sm"
            title="Mês anterior"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={goToCurrentMonth}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white rounded-md transition-all shadow-sm"
          >
            Hoje
          </button>
          <button
            onClick={goToNextMonth}
            className="p-2 hover:bg-white rounded-md transition-all shadow-sm"
            title="Próximo mês"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Calendário */}
      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-3 border-[#49943d] border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Dias da semana - estilo Google */}
          <div className="grid grid-cols-7 mb-1">
            {weekDays.map(day => (
              <div
                key={day}
                className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide py-3 bg-gray-50 rounded-t-lg"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Dias do mês */}
          <div className="grid grid-cols-7 border border-gray-200 rounded-b-lg overflow-hidden">
            {calendarDays.map((day, index) => {
              const appointmentCount = getDayAppointmentCount(day.date);
              const isSelected = selectedDate && day.date.toDateString() === selectedDate.toDateString();
              const timeSlots = getDayTimeSlots(day.date);

              // Obter horários disponíveis para este dia
              const dayDateString = day.isCurrentMonth ? formatLocalDate(day.date) : null;
              const availableSlots = dayDateString ? (getAvailableSlotsForDay[dayDateString] || []) : [];
              const availableCount = availableSlots.length;

              return (
                <div
                  key={index}
                  className={`
                    relative group min-h-[100px] sm:min-h-[120px] border-r border-b border-gray-100
                    ${!day.isCurrentMonth ? 'bg-gray-50' : ''}
                    ${day.isToday && day.isCurrentMonth ? 'bg-blue-50' : ''}
                  `}
                >
                  <button
                    onClick={() => day.isCurrentMonth && handleDateClick(day.date)}
                    disabled={!day.isCurrentMonth}
                    className={`
                      absolute top-2 left-2 w-8 h-8 flex items-center justify-center rounded-full transition-all
                      ${!day.isCurrentMonth
                        ? 'text-gray-300 cursor-not-allowed'
                        : day.isToday
                        ? 'bg-[#49943d] text-white font-semibold shadow-md'
                        : isSelected
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'hover:bg-gray-100'
                      }
                      ${appointmentCount > 0 && day.isCurrentMonth ? 'font-semibold' : ''}
                    `}
                  >
                    <span className="text-sm">{day.date.getDate()}</span>
                  </button>

                  {/* Indicador de disponíveis */}
                  {selectedUserId && day.isCurrentMonth && availableCount > 0 && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                  )}

                  {/* Lista de agendamentos */}
                  {appointmentCount > 0 && day.isCurrentMonth && (
                    <div className="mt-12 px-2 space-y-1">
                      {timeSlots.slice(0, 3).map((slot, slotIdx) => (
                        <div
                          key={slotIdx}
                          className="text-xs bg-blue-100 text-blue-800 truncate px-2 py-1 rounded-md font-medium"
                          title={`${slot.time} - ${slot.appointment.client_name} (Resp: ${slot.appointment.responsible_name || 'Sem resp.'})`}
                        >
                          <div>{slot.time} {slot.appointment.client_name?.substring(0, 10) || ''}</div>
                          {slot.appointment.responsible_name && !selectedUserId && (
                            <div className="text-[10px] text-blue-600 font-normal truncate">
                              👤 {slot.appointment.responsible_name.split(' ')[0]}
                            </div>
                          )}
                        </div>
                      ))}
                      {timeSlots.length > 3 && (
                        <div className="text-xs text-gray-500 px-2">
                          +{timeSlots.length - 3} mais
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tooltip hover com detalhes completos */}
                  {appointmentCount > 0 && timeSlots.length > 0 && (
                    <div className="hidden group-hover:block absolute z-20 top-full left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white text-gray-800 text-xs rounded-xl shadow-2xl border border-gray-200 p-3 pointer-events-none">
                      <div className="font-semibold text-sm text-gray-900 mb-2 pb-2 border-b border-gray-100">
                        {day.date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {timeSlots.map((slot, slotIdx) => {
                          const appointmentDate = parseAppointmentToLocalDate(slot.appointment);
                          const endTime = new Date(appointmentDate.getTime() + (slot.appointment.duration_minutes || 0) * 60000);
                          const endTimeStr = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;

                          return (
                            <div key={slotIdx} className="pb-2 border-b border-gray-100 last:border-0">
                              <div className="font-semibold text-blue-600 flex items-center gap-2">
                                {slot.time} - {endTimeStr}
                                {slot.appointment.internal_type && (
                                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[9px] font-medium">
                                    {slot.appointment.internal_type === 'REUNIAO' && 'Reunião'}
                                    {slot.appointment.internal_type === 'SERVICO_EXTERNO' && 'Serviço Ext.'}
                                    {slot.appointment.internal_type === 'AUSENCIA_TEMPORARIA' && 'Ausência'}
                                    {slot.appointment.internal_type === 'TREINAMENTO' && 'Treinamento'}
                                    {slot.appointment.internal_type === 'MANUTENCAO' && 'Manutenção'}
                                    {slot.appointment.internal_type === 'OUTRO' && 'Outro'}
                                  </span>
                                )}
                              </div>
                              <div className="text-gray-700 font-medium">
                                {slot.appointment.client_name}
                              </div>
                              {slot.appointment.responsible_name && (
                                <div className="text-xs text-blue-600 font-medium flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  Responsável: {slot.appointment.responsible_name}
                                </div>
                              )}
                              <div className="text-gray-400 text-[10px]">
                                {slot.appointment.duration_minutes} min
                                {slot.appointment.client_phone ? ` • ${slot.appointment.client_phone}` : ''}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legenda */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span>Agendamentos</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>Horários disponíveis</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Detalhes do dia selecionado */}
      {selectedDate && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 capitalize">
              {selectedDate.toLocaleDateString('pt-BR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          {getDayTimeSlots(selectedDate).length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {getDayTimeSlots(selectedDate).map((slot, idx) => {
                const appointmentDate = parseAppointmentToLocalDate(slot.appointment);
                const endTime = new Date(appointmentDate.getTime() + slot.appointment.duration_minutes * 60000);
                const endTimeStr = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;

                return (
                  <div
                    key={idx}
                    className={`flex items-start gap-4 p-4 bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow ${
                      slot.appointment.internal_type ? 'border-purple-200' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <div className={`rounded-lg px-3 py-2 text-center min-w-[70px] ${
                        slot.appointment.internal_type ? 'bg-purple-500' : 'bg-blue-500'
                      } text-white`}>
                        <div className="text-sm font-bold">{slot.time}</div>
                        <div className="text-[10px] opacity-80">até {endTimeStr}</div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 font-semibold text-gray-900 text-base mb-1">
                        {slot.appointment.client_name}
                        {slot.appointment.internal_type && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                            {slot.appointment.internal_type === 'REUNIAO' && 'Reunião'}
                            {slot.appointment.internal_type === 'SERVICO_EXTERNO' && 'Serviço Externo'}
                            {slot.appointment.internal_type === 'AUSENCIA_TEMPORARIA' && 'Ausência Temporária'}
                            {slot.appointment.internal_type === 'TREINAMENTO' && 'Treinamento'}
                            {slot.appointment.internal_type === 'MANUTENCAO' && 'Manutenção'}
                            {slot.appointment.internal_type === 'OUTRO' && 'Outro'}
                          </span>
                        )}
                      </div>
                      <div className="text-gray-500 text-sm mb-2">
                        Duração: {slot.appointment.duration_minutes} minutos
                      </div>
                      {slot.appointment.client_phone && (
                        <div className="text-gray-600 text-sm mb-1 flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {slot.appointment.client_phone}
                        </div>
                      )}
                      {slot.appointment.client_address && (
                        <div className="text-gray-600 text-sm mb-1 flex items-start gap-2">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{slot.appointment.client_address}</span>
                        </div>
                      )}
                      {slot.appointment.notes && (
                        <div className="text-gray-500 text-sm mt-2 pt-2 border-t border-gray-100">
                          <span className="font-medium">Obs:</span> {slot.appointment.notes}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Nenhum agendamento neste dia</p>
            </div>
          )}

          {/* Horários disponíveis */}
          {selectedUserId && (() => {
            const selectedDateString = formatLocalDate(selectedDate);
            const availableSlots = getAvailableSlotsForDay[selectedDateString] || [];

            if (availableSlots.length > 0) {
              return (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Horários Disponíveis ({availableSlots.length})
                  </h4>
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 bg-gray-50 rounded-lg">
                    {availableSlots.map((slot, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 text-sm bg-white text-gray-700 border border-gray-200 rounded-lg font-medium hover:bg-green-50 hover:border-green-200 transition-colors cursor-pointer"
                      >
                        {slot.formatted}
                      </span>
                    ))}
                  </div>
                </div>
              );
            } else if (selectedUserId) {
              return (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-400 italic">
                    Nenhum horário disponível. Verifique a jornada de trabalho.
                  </p>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendar;

